import { useMutation } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useRef } from "react";
import { useModals } from "react-easy-modals";
import * as yup from "yup";
import { colors, sizes, spacings } from "@/assets/themes";
import { useAuth } from "@/modules/hooks/use-auth";
import useToast from "@/modules/hooks/use-toast";
import polyglot from "@/utils/polyglot";
import Alert from "../alert";
import Avatar from "../avatar/index";
import Block from "../block";
import Button from "../button";
import ConditionnalWrapper from "../conditionnal-wrapper";
import FormControl from "../form-control";
import Input from "../input";
import Link from "../link";
import List from "../list/index";
import Modal from "../modal/index";
import { Body14, Body16, H5 } from "../text";
import SignUp from "./signup-modal";
import WrongCountryModal from "./wrong-country-modal";

const loginSchema = yup.object({
  user: yup.object({
    email: yup
      .string()
      .email(polyglot.t("common.errors.email_invalid"))
      .required(polyglot.t("common.errors.field_required")),
    password: yup.string().required(polyglot.t("common.errors.field_required")),
  }),
});

function Login({
  isOpen,
  close,
  redirectionPath,
  alertMessage,
  params,
  user,
  noModal = false,
  resolve,
  preventRedirect = false,
}) {
  const { signIn, redirect } = useAuth();
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const modals = useModals();
  const toast = useToast();
  const signInMutation = useMutation({
    mutationFn: (values) => signIn("credentials", values),
    onSuccess: (response) => {
      if (!preventRedirect) {
        redirect(redirectionPath);
      } else if (resolve) {
        // resolve is used to resolve promise from main modal
        resolve(response);
        // close this current modal after resolving the promise (and close the main modal)
        close(response);
      } else {
        close(response);
      }
    },
    onError: ({ response }, values) => {
      if (response.data.user_country_code && response.data.domain_country_code) {
        modals.open(WrongCountryModal, {
          userEmail: values.user.email,
          originCountryCode: response?.data?.user_country_code,
          domainCountryCode: response?.data?.domain_country_code,
          onSignUp: () =>
            modals.open(
              SignUp,
              {
                redirectionPath,
                params,
                preventRedirect,
                resolve: (result) => {
                  // resolve is used to resolve promise from main modal
                  resolve(result);
                  // close this current modal after resolving the promise (and close the main modal)
                  close(result);
                },
              },
              { replace: true }
            ),
          onRedirect: () => redirect(redirectionPath, response?.data),
        });
      } else {
        toast.danger(response.data.errorMessage);
        throw new Error(`${response.data.errorMessage}`);
      }
    },
  });

  useEffect(() => {
    if (passwordInputRef.current && isOpen) {
      setTimeout(() => {
        //monkey patch to focus the password input, it doesn't work with the props. its instant unfocused
        passwordInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const onSubmit = (values) => {
    signInMutation.mutate(values);
  };

  return (
    <ConditionnalWrapper
      condition={!noModal}
      wrapper={(children) => (
        <Modal.Small
          animate={modals.stack.length === 1}
          fullScreenOnMobile
          isOpen={isOpen}
          onClose={() => modals.closeAll()}
        >
          {children}
        </Modal.Small>
      )}
    >
      <Formik
        initialValues={{
          user: {
            email: user.email,
            password: user.password || "",
          },
          remember_me: true,
          ...params,
        }}
        onSubmit={onSubmit}
        validateOnChange
        validationSchema={loginSchema}
      >
        {({ values, errors, touched, handleChange, handleBlur, isValid }) => (
          <div>
            <Modal.Item.Header
              onBack={
                // eslint-disable-next-line no-nested-ternary
                noModal ? undefined : modals.stack.length === 1 ? undefined : () => modals.close()
              }
              onClose={
                // eslint-disable-next-line no-nested-ternary
                noModal
                  ? undefined
                  : modals.stack.length === 1
                    ? () => close()
                    : () => modals.closeAll()
              }
              title={polyglot.t("login.happy_to_see_you_again")}
            />
            <Modal.Item.Wrapper>
              <Block spaceY={spacings.xs}>
                <List.Item
                  divider={false}
                  LeftComponent={() => <Avatar size={sizes.size96} src={user.avatar} />}
                >
                  <H5>{user.first_name}</H5>
                  <Body16 color={colors.muted}>{user.email}</Body16>
                </List.Item>
                <Form noValidate>
                  <Block display="none">
                    <Input
                      name="user.email"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder={polyglot.t("common.placeholder.email")}
                      type="email"
                      value={values.user.email}
                    />
                  </Block>

                  <Block display="flex" flexDirection="column" gap={spacings.s}>
                    <FormControl
                      error={
                        touched.user?.password && errors.user?.password
                          ? errors.user?.password
                          : undefined
                      }
                      label={polyglot.t("common.password")}
                    >
                      <Input
                        name="user.password"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        placeholder={polyglot.t("common.placeholder.password")}
                        ref={passwordInputRef}
                        type="password"
                        value={values.user.password}
                      />
                    </FormControl>
                  </Block>

                  {alertMessage && (
                    <Block marginBottom={spacings.s}>
                      <Alert.Low>{alertMessage}</Alert.Low>
                    </Block>
                  )}

                  <Block alignItems="center" display="flex" flexDirection="column" gap={spacings.m}>
                    <Button.Large
                      block
                      disabled={!isValid}
                      isLoading={signInMutation.isPending || signInMutation.isSuccess}
                      type="submit"
                    >
                      {polyglot.t("common.to_login")}
                    </Button.Large>
                    <Link href="/password/new?context=user" target="_blank">
                      <Body14>{polyglot.t("login.forgot_password")}</Body14>
                    </Link>
                  </Block>
                </Form>
              </Block>
            </Modal.Item.Wrapper>
          </div>
        )}
      </Formik>
    </ConditionnalWrapper>
  );
}

export default Login;
